// Slack-style inline mention references.
//
// A mention is stored in the message text as an immutable id reference —
// `<@u:HUMAN_ID>` for a person, `<@a:AGENT_INSTANCE_ID>` for an agent — never a
// display name. Clients resolve the id to the *current* name at render time, so
// a rename updates every past @mention and two people with the same name never
// collide. (This is exactly how Slack's `<@U123>` refs work.)
//
// The composer is a plain <textarea>, so we can't show a chip while typing; it
// shows `@Display_Name` tokens (what the @-picker inserts) and we convert:
//   - on send: displayTokensToRefs()  — @Token → <@u:id>/<@a:id>
//   - on edit-load: refsToDisplay()   — <@u:id> → @Name (so the raw ref never
//     shows in the editor)
// and the renderer turns refs into chips via the remarkMentions plugin.

export type MentionKind = 'u' | 'a' | 'h'

// A roster entry the composer resolves @tokens against. 'helper' is the
// @helper summons (HELPER_MEMBER), not a person or an agent.
export type MentionMember = { id: string; name: string; kind: 'human' | 'agent' | 'helper' }

// @helper summons a fresh throwaway agent into the thread (docs/quick-helper-
// plan.md). Stored as its own ref kind, <@h:new>, so it can never resolve to a
// real member however someone names themselves or an agent.
export const HELPER_MEMBER: MentionMember = { id: 'new', name: 'helper', kind: 'helper' }
export const HELPER_REF = '<@h:new>'

const REF_KIND: Record<MentionMember['kind'], MentionKind> = { human: 'u', agent: 'a', helper: 'h' }

// Resolve a ref to its current display name (+ whether it's the viewer). Return
// null for an unknown id so the raw ref is left alone.
export type MentionResolver = (
  kind: MentionKind,
  id: string,
) => { name: string; isSelf: boolean } | null

// Matches a stored mention ref. id is anything up to the closing '>' that isn't
// whitespace or '>'. Angle-bracket form is inert in GFM (not a valid HTML tag —
// tag names can't start with '@' — and not an autolink), so react-markdown keeps
// it as literal text for the remark plugin to pick up.
export const MENTION_REF_RE = /<@([uah]):([^>\s]+)>/g

// A picked @token: '@' + display name with spaces collapsed to underscores.
// Kept to word chars to match the existing server-side parser semantics.
const MENTION_TOKEN_RE = /@(\w+)/g

function normalizeName(name: string): string {
  return name.replace(/\s+/g, '_').toLowerCase()
}

// displayTokensToRefs converts every @token that resolves against the roster
// into an id ref. Unresolved tokens (typed by hand, or a name no longer present)
// are left as-is. Agents win ties over humans only if listed first — callers
// pass humans first so a human/agent name clash resolves to the human.
export function displayTokensToRefs(text: string, roster: MentionMember[]): string {
  if (!roster.length) return text
  const byNorm = new Map<string, MentionMember>()
  for (const m of roster) {
    const key = normalizeName(m.name)
    if (!byNorm.has(key)) byNorm.set(key, m) // first wins
  }
  return text.replace(MENTION_TOKEN_RE, (whole, token: string) => {
    const m = byNorm.get(token.toLowerCase())
    if (!m) return whole
    return `<@${REF_KIND[m.kind]}:${m.id}>`
  })
}

// refsToDisplay converts id refs back to `@Name` plain text — for the edit
// composer and for non-markdown surfaces (reply-quote previews). Unknown ids are
// left as the raw ref.
export function refsToDisplay(text: string, resolve: MentionResolver): string {
  return text.replace(MENTION_REF_RE, (whole, kind: MentionKind, id: string) => {
    const r = resolve(kind, id)
    if (!r) return whole
    return '@' + r.name.replace(/\s+/g, '_')
  })
}

// mentionsSelf reports whether the text mentions the viewer, for the
// highlight-when-mentioned bubble treatment.
export function mentionsSelf(text: string, resolve: MentionResolver): boolean {
  MENTION_REF_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = MENTION_REF_RE.exec(text)) !== null) {
    const r = resolve(m[1] as MentionKind, m[2])
    if (r?.isSelf) return true
  }
  return false
}

// Minimal mdast node shape for the tree walk (avoids a @types/mdast dep).
type MdNode = { type: string; value?: string; children?: MdNode[]; data?: unknown }

// remarkMentions splits mention refs out of text nodes into styled chip nodes
// (a <span class="md-mention"> carrying the resolved @name; self-mentions get
// md-mention--self). Runs only on prose text — code spans/blocks are leaf nodes
// we never descend into, so a ref shown as an example inside code stays literal.
export function remarkMentions(resolve: MentionResolver) {
  // Returns a unified *plugin* (a function that returns the transformer), so it
  // can be dropped straight into remarkPlugins alongside remarkGfm/remarkEmoji.
  return () => (tree: MdNode) => walk(tree, resolve)
}

function walk(node: MdNode, resolve: MentionResolver): void {
  const children = node.children
  if (!children) return
  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    if (child.type === 'text' && typeof child.value === 'string' && child.value.includes('<@')) {
      const replacement = splitMentions(child.value, resolve)
      if (replacement) {
        children.splice(i, 1, ...replacement)
        i += replacement.length - 1
      }
    } else if (child.type !== 'code' && child.type !== 'inlineCode') {
      walk(child, resolve)
    }
  }
}

// splitMentions turns a text value into an array of text + mention nodes, or
// null if it holds no resolvable refs.
function splitMentions(value: string, resolve: MentionResolver): MdNode[] | null {
  MENTION_REF_RE.lastIndex = 0
  const out: MdNode[] = []
  let last = 0
  let matched = false
  let m: RegExpExecArray | null
  while ((m = MENTION_REF_RE.exec(value)) !== null) {
    const r = resolve(m[1] as MentionKind, m[2])
    if (!r) continue // unknown id → leave inline as literal text
    matched = true
    if (m.index > last) out.push({ type: 'text', value: value.slice(last, m.index) })
    out.push({
      type: 'mention',
      data: {
        hName: 'span',
        hProperties: { className: 'md-mention' + (r.isSelf ? ' md-mention--self' : '') },
      },
      children: [{ type: 'text', value: '@' + r.name }],
    })
    last = m.index + m[0].length
  }
  if (!matched) return null
  if (last < value.length) out.push({ type: 'text', value: value.slice(last) })
  return out
}
